import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText } from 'lucide-react';
import { BACKEND_URL } from '@/config';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

const ACTION_LABELS = {
  // Admin actions
  VERIFY_USER:              { label: 'Vérification',      bg: 'var(--tbg)',   color: 'var(--teal)' },
  SUSPEND_USER:             { label: 'Suspension',        bg: '#fef2f2',      color: 'var(--red)' },
  REACTIVATE_USER:          { label: 'Réactivation',      bg: 'var(--tbg)',   color: 'var(--teal)' },
  KYC_APPROVE:              { label: 'KYC approuvé',      bg: 'var(--tbg)',   color: 'var(--teal)' },
  KYC_REJECT:               { label: 'KYC rejeté',        bg: '#fef2f2',      color: 'var(--red)' },
  RESOLVE_DISPUTE_REFUND:   { label: 'Remboursement',     bg: '#ede9fe',      color: '#5b21b6' },
  RESOLVE_DISPUTE_RELEASE:  { label: 'Libération fonds',  bg: 'var(--gpale)', color: 'var(--gold)' },
  REFUND_PAYMENT:           { label: 'Remboursement',     bg: '#fef2f2',      color: 'var(--red)' },
  RELEASE_PAYMENT:          { label: 'Libération',        bg: 'var(--tbg)',   color: 'var(--teal)' },
  HIDE_RATING:              { label: 'Masquer avis',      bg: '#f1f5f9',      color: 'var(--ink2)' },
  QR_BYPASS_PICKUP:         { label: 'Bypass QR remise',  bg: '#fef9c3',      color: '#92400e' },
  QR_BYPASS_DELIVERY:       { label: 'Bypass QR livraison', bg: '#fef9c3',    color: '#92400e' },
  ACCOUNT_DELETED:          { label: 'Compte supprimé',   bg: '#fef2f2',      color: 'var(--red)' },
  // Mission lifecycle
  MISSION_CREATED:          { label: 'Mission créée',     bg: '#f0fdf4',      color: '#166534' },
  MISSION_ACCEPTED:         { label: 'Mission acceptée',  bg: 'var(--tbg)',   color: 'var(--teal)' },
  MISSION_DECLINED:         { label: 'Mission déclinée',  bg: '#fef2f2',      color: 'var(--red)' },
  MISSION_CANCELLED:        { label: 'Mission annulée',   bg: '#fef2f2',      color: 'var(--red)' },
  MISSION_IN_TRANSIT:       { label: 'En transit',        bg: '#eff6ff',      color: '#1d4ed8' },
  MISSION_DELIVERED:        { label: 'Livrée',            bg: '#f0fdf4',      color: '#166534' },
  MISSION_COMPLETED:        { label: 'Terminée ✓',        bg: 'var(--gpale)', color: 'var(--gold)' },
  // Proposition lifecycle
  PROPOSITION_CREATED:      { label: 'Proposition',       bg: '#f5f3ff',      color: '#6d28d9' },
  PROPOSITION_ACCEPTED:     { label: 'Prop. acceptée',    bg: 'var(--tbg)',   color: 'var(--teal)' },
  PROPOSITION_REJECTED:     { label: 'Prop. refusée',     bg: '#fef2f2',      color: 'var(--red)' },
  PROPOSITION_WITHDRAWN:    { label: 'Prop. retirée',     bg: '#f1f5f9',      color: 'var(--ink2)' },
  // Payment lifecycle
  PAYMENT_RESERVED:         { label: 'Paiement réservé',  bg: '#eff6ff',      color: '#1d4ed8' },
  PAYMENT_CAPTURED:         { label: 'Paiement capturé',  bg: 'var(--tbg)',   color: 'var(--teal)' },
  PAYMENT_RESERVATION_CANCELLED: { label: 'Résa annulée', bg: '#fef2f2',     color: 'var(--red)' },
  PAYMENT_CHECKOUT_INITIATED: { label: 'Checkout initié', bg: '#f5f3ff',      color: '#6d28d9' },
};

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/audit`, { params: { page, limit: 30 } })
      .then(r => { setLogs(r.data.data || []); setTotal(r.data.meta?.total || 0); })
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: 'var(--f-display)', fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Journal d'audit</div>
        <div style={{ fontSize: 14, color: 'var(--ink2)' }}>{total} événements</div>
      </div>

      <div className="card">
        {loading ? <div className="skeleton" style={{ height: 300 }} /> : (
          logs.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px 0' }}>
              <FileText size={48} style={{ opacity: .3, marginBottom: 12 }} />
              <div>Aucun événement d'audit</div>
            </div>
          ) : (
            <table className="c2s-table">
              <thead>
                <tr><th>Date</th><th>Admin</th><th>Action</th><th>Entité</th><th>ID</th></tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const style = ACTION_LABELS[log.action] || { label: log.action, bg: '#f1f5f9', color: 'var(--ink2)' };
                  return (
                    <tr key={log.id}>
                      <td style={{ fontSize: 12, color: 'var(--ink3)', whiteSpace: 'nowrap' }}>{formatDate(log.created_at)}</td>
                      <td style={{ fontSize: 13 }}>{log.user_name || 'Système'}</td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: style.bg, color: style.color }}>
                          {style.label}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>{log.entity_type}</td>
                      <td style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--ink3)' }}>{log.entity_id?.slice(-8) || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}

        {total > 30 && (
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
            <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Préc.</button>
            <span style={{ fontSize: 13, alignSelf: 'center' }}>Page {page} / {Math.ceil(total / 30)}</span>
            <button className="btn btn-ghost btn-sm" disabled={page >= Math.ceil(total / 30)} onClick={() => setPage(p => p + 1)}>Suiv. →</button>
          </div>
        )}
      </div>
    </div>
  );
}
